import type { SetupBlock } from './aiAgentAvailability'

/**
 * One day of availability as a room board: the same shape the Action Center
 * "Open chair time" board draws, so a practice reads both the same way.
 * Columns are rooms, the clock runs down, and each block is a provider's
 * time in that room.
 */

export type Minutes = number

export type BoardBlock = SetupBlock & {
  start: Minutes
  end: Minutes
  /** Side-by-side lane when two blocks overlap in the same room. */
  lane: number
  lanes: number
}

export type BoardColumn = { id: string; label: string; blocks: BoardBlock[] }

export type DayBoard = {
  /** Only the rooms in use this day, in the week's room order. */
  columns: BoardColumn[]
  /** The clock runs from the hour the first block starts to the hour the last one ends. */
  startMinute: Minutes
  endMinute: Minutes
}

const DEFAULT_START = 8 * 60
const DEFAULT_END = 17 * 60

export function toMinutes(clock: string): Minutes {
  const [h, m] = clock.split(`:`).map(Number)
  return h * 60 + (m || 0)
}

/** Blocks that overlap share the room width; each gets a lane. */
function assignLanes(blocks: BoardBlock[]): BoardBlock[] {
  const sorted = [...blocks].sort((a, b) => a.start - b.start || a.end - b.end)
  const laneEnds: Minutes[] = []
  const clusters: BoardBlock[][] = []
  let cluster: BoardBlock[] = []
  let clusterEnd = -1
  for (const block of sorted) {
    if (block.start >= clusterEnd && cluster.length) {
      clusters.push(cluster)
      cluster = []
      laneEnds.length = 0
    }
    let lane = laneEnds.findIndex((end) => end <= block.start)
    if (lane === -1) {
      lane = laneEnds.length
      laneEnds.push(block.end)
    } else {
      laneEnds[lane] = block.end
    }
    block.lane = lane
    cluster.push(block)
    clusterEnd = Math.max(clusterEnd, block.end)
  }
  if (cluster.length) clusters.push(cluster)
  for (const group of clusters) {
    const lanes = Math.max(...group.map((b) => b.lane)) + 1
    for (const block of group) block.lanes = lanes
  }
  return sorted
}

/** Lay one day out by room. `rooms` is every room the week knows, which sets the column order. */
export function buildDayBoard(blocks: SetupBlock[], rooms: { id: string; label: string }[]): DayBoard {
  const byRoom = new Map<string, BoardBlock[]>()
  for (const block of blocks) {
    const list = byRoom.get(block.operatory) ?? []
    list.push({ ...block, start: toMinutes(block.from), end: toMinutes(block.to), lane: 0, lanes: 1 })
    byRoom.set(block.operatory, list)
  }
  const known = new Set(rooms.map((r) => r.label))
  const extra = [...byRoom.keys()].filter((label) => !known.has(label)).map((label) => ({ id: label, label }))
  const columns: BoardColumn[] = [...rooms, ...extra]
    .map((room) => ({ id: room.id, label: room.label, blocks: assignLanes(byRoom.get(room.label) ?? []) }))
    .filter((column) => column.blocks.length > 0)

  const all = columns.flatMap((c) => c.blocks)
  if (!all.length) return { columns, startMinute: DEFAULT_START, endMinute: DEFAULT_END }
  const startMinute = Math.floor(Math.min(...all.map((b) => b.start)) / 60) * 60
  const endMinute = Math.ceil(Math.max(...all.map((b) => b.end)) / 60) * 60
  return { columns, startMinute, endMinute: Math.max(endMinute, startMinute + 60) }
}

export function hourMarks(startMinute: Minutes, endMinute: Minutes): Minutes[] {
  const marks: Minutes[] = []
  for (let minute = startMinute; minute <= endMinute; minute += 60) marks.push(minute)
  return marks
}

/** "9 AM" on the clock rail and "9 - 11:30 AM" on a block, as the schedule board writes them. */
export function formatMinutes(minutes: Minutes): string {
  const h24 = Math.floor(minutes / 60)
  const m = minutes % 60
  const suffix = h24 < 12 ? `AM` : `PM`
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12
  return `${h12}:${String(m).padStart(2, `0`)} ${suffix}`
}

export function formatRange(start: Minutes, end: Minutes): string {
  const trim = (label: string) => label.replace(`:00`, ``)
  const from = trim(formatMinutes(start))
  const to = trim(formatMinutes(end))
  const [fromClock, fromMeridiem] = from.split(` `)
  const [, toMeridiem] = to.split(` `)
  return fromMeridiem === toMeridiem ? `${fromClock} - ${to}` : `${from} - ${to}`
}
